import { ChangeEvent, Dispatch, FocusEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { Link } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { connect, useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';
import cn from 'classnames';
import { FormSpy } from 'react-final-form';
import { getIn } from 'final-form';

import { AutoComplete } from 'UIPackage/MainModule/components/AutoComplete';
import { IKnmIndex } from 'APIPackage/KnmModule/interfaces/IKnmIndex';
import { IKnmLink } from 'APIPackage/KnmModule/interfaces/IKnmLink';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { ROUTE_DECISION, ROUTE_KNM, ROUTE_PM } from 'CorePackage/MainModule/routes/routes';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmListFilterParams } from 'APIPackage/KnmListModule/interfaces/IKnmListFilterParams';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { coreSetLoading } from 'CorePackage/MainModule/actions/coreActions';
import {
  getKnmData,
  getKnmFormRef,
  getKnmIsEditableInStatusCommentsExist,
  getKnmSystemType,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { formatDate, formatDateLocale } from 'CorePackage/MainModule/common/formatDate';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import {
  hasKnmUnresolvedViolations,
  knmListFindKnm,
  knmListSetFindData,
} from 'KnmListPackage/MainModule/actions/knmListActions';
import { getKnmListFindData } from 'KnmListPackage/MainModule/selectors/knmListSelectors';
import { Row } from 'UIPackage/FormModule/components/Row';
import { knmFillOrganizationsAndObjectsByOtherPassport } from 'KnmPackage/MainModule/actions/knmActions';
import { KnmSystemTypesEnum } from 'KnmPackage/MainModule/enums/KnmSystemTypesEnum';
import { knmPmFillOrganizationsAndObjectsByOtherPassport } from 'KnmPmPackage/MainModule/actions/knmPmActions';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IKnmSubject248 } from 'APIPackage/KnmModule/interfaces/IKnmSubject248';
import { IKnmObject248 } from 'APIPackage/KnmModule/interfaces/IKnmObject248';
import { IKnmEvent248 } from 'APIPackage/KnmModule/interfaces/IKnmEvent248';
import { IKnmRequirementsNpa248 } from 'APIPackage/KnmModule/interfaces/IKnmRequirementsNpa248';
import {
  INFORMATION_ABOUT_COPY_DATA_BY_PRESCRIPTION,
  INFORMATION_ABOUT_LINK_DATA_KNM,
  INFORMATION_ABOUT_LINK_DATA_PM,
} from 'CorePackage/MainModule/constants/notices';
import { IKnmRequirementsNpa } from 'APIPackage/KnmModule/interfaces/IKnmRequirementsNpa';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';
import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { KNM_KIND_TITLE_BY_CODE } from 'APIPackage/DictionariesModule/constants/knmKindTitleByCode';
import { setIsNpaStructureChanged } from 'KnmPackage/RequirementsNpaModalModule/actions/requirementsNpaActions';

import styles from './ErknmLinks.module.scss';
import { requiredRequirementsErknmOrApproveDocsOrLinks } from '../../../../CorePackage/MainModule/validators/requiredRequirementsErknmOrApproveDocsOrLinks';
import { FieldBlock } from '../../../../UIPackage/FormModule/components/FieldBlock';
import { IKnm } from '../../../../APIPackage/KnmModule/interfaces/IKnm';
import { fetchKnm } from '../../../../APIPackage/KnmModule/services/knmApi';
import { IKnmErknmFormReason } from '../../../MainModule/interfaces/IKnmErknmFormReason';
import { getDecisionFormRef } from '../../../../DecisionPackage/MainModule/selectors/decisionSelectors';
import { getTitleFieldWithRedStar } from '../../../../functions/getTitleFieldWithRedStar';
import { isPrescriptionKindDecisionCode } from './functions/prescriptions';
import { validateSelectedLinkKnm } from './functions/validateSelectedLinkKnm';
import { validateSelectedLinkDecision } from './functions/validateSelectedLinkDecision';
import { useLinkPrescriptions } from './hooks/useLinkPrescriptions';
import { LinkPrescriptions } from './components/LinkPrescriptions';
import { getKnmErknmIsPrescriptionOldRules } from '../../../MainModule/selectors/knmErknmSelectors';
import {
  knmErknmSetPrescriptionFailed,
  knmErknmSetPrescriptionOldRules,
} from '../../../MainModule/actions/knmErknmActions';
import { KindResultDecisionCodesEnum } from '../../../../APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { IDecisionListItem } from '../../../../DecisionListPackage/MainModule/interfaces/IDecisionListItem';
import { getDecisionListData } from '../../../../DecisionListPackage/MainModule/selectors/decisionListSelectors';
import {
  decisionListGetData,
  decisionListSetSearchValue,
} from '../../../../DecisionListPackage/MainModule/actions/decisionListActions';
import {
  isSourceCreatedByOldRules,
  LEGACY_NO_PREFILL,
} from '../../../../CorePackage/MainModule/common/prescriptionLegacy';

const ERP_ID_MIN_LENGTH = 12;
const SEARCH_DEBOUNCE_MS = 3000;
const DECISION_STATUS_COMPLETED_ID = 7;

interface IKnmAutocompleteOption {
  label: string;
  value: string;
  knm?: IKnmIndex;
  decision?: IDecisionListItem;
}

const generateKnmInfoText = (knm: IKnmIndex) =>
  `${knm.erpId ? `${knm.erpId} ` : ''}${knm.knmType ?? ''} ${knm.organizationName ?? ''} ${
    knm.inn ? `(ИНН: ${knm.inn}` : ''
  }${knm.ogrn ? ` ОГРН: ${knm.ogrn}` : ''}) ${knm.controllingOrganization ?? ''}${
    knm.prosecutorOrganization ?? ''
  } ${formatDateLocale({
    date: knm.startDate,
    dateFormat: DATE_FORMAT_LONG_FNS,
  })} - ${knm.stopDate ? `${formatDateLocale({ date: knm.stopDate, dateFormat: DATE_FORMAT_LONG_FNS })}` : '?'}`;

const generateDecisionInfoText = (decision: IDecisionListItem) =>
  `${decision.number ?? ''} ${decision.organizationName ?? ''} ${decision.kno ?? ''}${
    decision.prosecutorOrganization ?? ''
  } ${formatDateLocale({ date: decision.decisionDate, dateFormat: DATE_FORMAT_LONG_FNS })}`;

const getKnmType = (knmType?: string | null): string => {
  if (!knmType) return '';

  return knmType === 'Decision' ? 'Иное решение' : knmType;
};

const generateLinkInfoText = (knmLink: IKnmLink) =>
  `${getKnmType(knmLink?.knmType)} ${knmLink.subjectName ?? ''} ${knmLink.inn ? `(ИНН: ${knmLink.inn})` : ''}${
    knmLink.ogrn ? ` (ОГРН: ${knmLink.ogrn})` : ''
  } ${knmLink.knoOrganizationName ?? ''} ${knmLink.prosecutorOfficeName ?? ''} ${formatDate({
    date: knmLink.startDate,
    dateFormat: DATE_FORMAT_LONG_FNS,
  })} - ${knmLink.stopDate ? `${formatDate({ date: knmLink.stopDate, dateFormat: DATE_FORMAT_LONG_FNS })}` : '?'}`;

interface IErknmLinksProps {
  watchOnly: boolean;
  isEnabled: boolean;
  isAddDisabled?: boolean;
  linksName?: string;
  reasonsFieldName?: string;
  reason?: IKnmErknmFormReason;
  created?: string | null;
  controllingOrganizationIds?: string;
  kindControlCode?: string;
  isErp?: boolean;
  kindKnmCode?: KnmKindCodesEnum;
}

interface IErknmLinksStoreProps {
  formRef: IKnmFormRef | undefined;
  knms: IKnmIndex[];
  isAvailableInStatusCommentsExist: boolean;
  settings: IAdminSetting[];
  isPm: boolean;
  isKnm: boolean;
  isDecision: boolean;
  passportId?: number;
  decisions: IDecisionListItem[];
  isPassportByCheckPrescriptionOldRules: boolean | undefined;
}

/**
 * Функция обогащения данных флагом isFilledFromLink
 */
function enrichmentData<T>(array: T[] | null, notNull?: boolean): T[] | null {
  if (!array) return notNull ? [] : null;

  array.map(item => {
    return { ...item, isFilledFromLink: true };
  });

  return array;
}

const ErknmLinksWithoutStore = ({
  watchOnly,
  isEnabled,
  isAddDisabled,
  linksName,
  formRef,
  knms,
  isAvailableInStatusCommentsExist,
  reasonsFieldName,
  reason,
  settings,
  created,
  controllingOrganizationIds,
  kindControlCode,
  isPm,
  isKnm,
  isDecision,
  passportId,
  isErp,
  kindKnmCode,
  decisions,
  isPassportByCheckPrescriptionOldRules,
}: IErknmLinksProps & IErknmLinksStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const erknmStartDateString = formRef?.current.getFieldState('startDate')?.value;

  const [isAddingFieldsActive, setIsAddingFieldsActive] = useState(false);
  const [knmsAutocompleteOptions, setKnmsAutocompleteOptions] = useState<IKnmAutocompleteOption[] | undefined>();
  const [erpIdInputValue, setErpIdInputValue] = useState('');
  const [isErpIdSelected, setIsErpIdSelected] = useState(false);
  const [lastKnmsAutocompleteOptions, setLastKnmsAutocompleteOptions] = useState<IKnmAutocompleteOption[]>([]);
  const [isFounded, setIsFounded] = useState(false);
  const [isKnmHasUnresolvedViolations, setIsKnmHasUnresolvedViolations] = useState<boolean | undefined>(undefined);
  const linkValidationFieldName = `${reasonsFieldName}.linkValidation`;

  const [selectedKnmForLink, setSelectedKnmForLink] = useState<IKnmIndex | undefined>();
  const [selectedLinkKnmCache, setSelectedLinkKnmCache] = useState<IKnm | undefined>();

  const {
    options: prescriptionOptions,
    selectedPrescriptionKeys,
    isFilled: isPrescriptionsFilled,
    isEditable: isPrescriptionEditable,
    isApplied: isPrescriptionsApplied,
    isChanged: isPrescriptionsChanged,
    reset: resetPrescriptions,
    fill: fillPrescriptions,
    markApplied: markPrescriptionsApplied,
  } = useLinkPrescriptions(formRef);

  const isPrescriptionCheckReason = reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK;
  const isImpossibilityOfPrevious = reason?.type?.name === KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS;
  const isSourceRaid = selectedKnmForLink?.kind === KNM_KIND_TITLE_BY_CODE[KnmKindCodesEnum.RO];
  const isCurrentRaid = kindKnmCode === KnmKindCodesEnum.RO;
  /** [4.1.31] Исходный паспорт создан до включения настройки — разделы не предзаполняются */
  const isSourceOldRules = Boolean(
    selectedLinkKnmCache && isSourceCreatedByOldRules(selectedLinkKnmCache.created, settings),
  );

  /**
   * Поле "Предписание" живёт, пока выбран паспорт рейдового осмотра: после сохранения ссылки
   * оно остаётся доступным.
   */
  const isPrescriptionPickerVisible = isPrescriptionCheckReason && isSourceRaid && !isErp && !isSourceOldRules;

  /** Флаг смены значений уже добавленных предписаний — нужно повторно применить их к паспорту */
  const isReapplyPrescriptions = isPrescriptionsApplied && isPrescriptionsChanged;

  const resetPrescriptionState = useCallback(() => {
    setSelectedKnmForLink(undefined);
    setSelectedLinkKnmCache(undefined);
    resetPrescriptions();
  }, [resetPrescriptions]);

  /** Предписания сохранённой ссылки при очистке поля поиска не сбрасываем — они относятся к ней */
  const resetNotAppliedPrescriptionState = useCallback(() => {
    if (!isPrescriptionsApplied) {
      resetPrescriptionState();
    }
  }, [isPrescriptionsApplied, resetPrescriptionState]);

  const closeLinkFields = useCallback(() => {
    setIsAddingFieldsActive(false);
    setErpIdInputValue('');
    resetNotAppliedPrescriptionState();
  }, [resetNotAppliedPrescriptionState]);

  const isEnablePaperlessDocument = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  // ТЗ 4.1.31: паспорта решений (ИР) ищутся в дополнение к КНМ/ПМ, но только в форме КНМ
  // (не ПМ, не ЕРП) с основанием «Проверка исполнения предписания».
  const isDecisionSearchEnabled = isEnablePaperlessDocument && isPrescriptionCheckReason && !isErp && isKnm;

  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );

  const isNeedNapValidation = useMemo(() => {
    // Перепроверка НПА есть только в блоке требований ЕРКНМ, в ЕРП флаг некому обработать
    if (isErp) return false;

    const hasPrescriptionCheckinLinkedKnm = selectedKnmForLink?.reasonsList?.find(
      item => item?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK,
    );

    return (
      (isPrescriptionCheckReason && !isEnablePrescriptionCheck) ||
      (isImpossibilityOfPrevious && !hasPrescriptionCheckinLinkedKnm)
    );
  }, [selectedKnmForLink, isEnablePrescriptionCheck, isPrescriptionCheckReason, isImpossibilityOfPrevious, isErp]);

  function findKnms(paramErpId?: string) {
    if (paramErpId && paramErpId.length >= ERP_ID_MIN_LENGTH) {
      setIsFounded(true);

      // ТЗ 4.1.31 (1.1): поиск паспортов решений по номеру, органу контроля, виду контроля
      // и статусу «Завершено», в дополнение к поиску КНМ/ПМ ниже.
      if (isDecisionSearchEnabled) {
        dispatch(
          decisionListSetSearchValue(paramErpId, {
            resultDecisionsKind: [KindResultDecisionCodesEnum.D_7, KindResultDecisionCodesEnum.D_1],
            controlKindCode: kindControlCode,
            knoIds: controllingOrganizationIds ? [controllingOrganizationIds] : undefined,
            statusIds: [DECISION_STATUS_COMPLETED_ID],
          }),
        );
        dispatch(decisionListGetData());
      }

      let params: IKnmListFilterParams = {};

      if (isEnablePaperlessDocument && controllingOrganizationIds) {
        switch (reason?.type?.name) {
          case KnmReasonTypeNameEnum.PRESCRIPTION_CHECK:
            params = {
              searchString: paramErpId,
              controllingOrganizationIds: [controllingOrganizationIds],
              supervisionTypeId: kindControlCode,
              isKnmLinkQuery: true,
              kinds: [
                'Профилактический визит',
                'Выборочный контроль',
                'Мониторинговая закупка',
                'Контрольная закупка',
                'Инспекционный визит',
                'Документарная проверка',
                'Выездная проверка',
                'Рейдовый осмотр',
              ],
            };
            break;
          case KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS:
            params = {
              erpId: paramErpId,
              isKnmLinkQuery: true,
              controllingOrganizationIds: [controllingOrganizationIds],
              supervisionTypeId: kindControlCode,
            };
            break;
          default:
            params = { erpId: paramErpId, isKnmLinkQuery: true, ignoreProsecutorOffice: true, ignoreKno: true };
            break;
        }
      } else {
        params = {
          erpId: paramErpId,
          isKnmLinkQuery: true,
          ignoreProsecutorOffice: true,
          ignoreKno: true,
        };
      }
      dispatch(knmListFindKnm(params, 2000));
    }
  }

  const findKnmsRef = useRef(findKnms);

  findKnmsRef.current = findKnms;

  const debouncedGetKnms = useMemo(
    () => debounce((paramErpId?: string) => findKnmsRef.current(paramErpId), SEARCH_DEBOUNCE_MS),
    [],
  );

  const handleTextInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const valueEdited = value?.trim();

      setErpIdInputValue(valueEdited);
      if (!valueEdited) {
        setIsErpIdSelected(false);
        resetNotAppliedPrescriptionState();
      }

      debouncedGetKnms(valueEdited);
      if (!valueEdited || valueEdited.length <= ERP_ID_MIN_LENGTH) {
        setKnmsAutocompleteOptions(undefined);
      }

      setIsKnmHasUnresolvedViolations(undefined);
    },
    [debouncedGetKnms, resetNotAppliedPrescriptionState],
  );

  /** Гонка при быстрой смене выбранного паспорта, результат устаревшего выбора игнорируем */
  const selectRequestIdRef = useRef(0);

  const handleAutoCompleteSelect = useCallback(
    (value: string) => async () => {
      const selectRequestId = selectRequestIdRef.current + 1;

      selectRequestIdRef.current = selectRequestId;
      setErpIdInputValue(value);

      const selectedOption = knmsAutocompleteOptions?.find(option => option.value === value);
      const knmId = selectedOption?.knm?.id;
      const hasPrescription = selectedOption?.knm?.erknmResultKindDecisionIds?.some(isPrescriptionKindDecisionCode);

      if (!isErp && knmId && isPrescriptionCheckReason && !hasPrescription) {
        const hasUnresolvedViolations = await new Promise<boolean>(resolve => {
          dispatch(hasKnmUnresolvedViolations(knmId.toString(), resolve));
        });

        if (selectRequestId !== selectRequestIdRef.current) return;

        setIsKnmHasUnresolvedViolations(hasUnresolvedViolations);
      } else {
        setIsKnmHasUnresolvedViolations(undefined);
      }

      debouncedGetKnms('');
      setKnmsAutocompleteOptions(undefined);
      setIsErpIdSelected(true);
      setSelectedKnmForLink(selectedOption?.knm);

      const isSelectedKnmRaid = selectedOption?.knm?.kind === KNM_KIND_TITLE_BY_CODE[KnmKindCodesEnum.RO];

      if (isPrescriptionCheckReason && isSelectedKnmRaid && knmId) {
        const linkedKnm: IKnm = await fetchKnm(knmId);

        if (selectRequestId !== selectRequestIdRef.current) return;

        setSelectedLinkKnmCache(linkedKnm);

        if (isSourceCreatedByOldRules(linkedKnm.created, settings)) {
          resetPrescriptions();
        } else {
          const isEnablePrescriptionCheckOfLinkedKnm = isLaterThanAdminSetting(
            linkedKnm.created,
            settings,
            AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
          );

          fillPrescriptions(linkedKnm, isEnablePrescriptionCheckOfLinkedKnm, erknmStartDateString);
        }
      } else {
        setSelectedLinkKnmCache(undefined);
        resetPrescriptions();
      }
    },
    [
      debouncedGetKnms,
      dispatch,
      knmsAutocompleteOptions,
      isPrescriptionCheckReason,
      settings,
      isErp,
      erknmStartDateString,
      fillPrescriptions,
      resetPrescriptions,
    ],
  );

  useEffect(() => {
    if (!isAddingFieldsActive) {
      setErpIdInputValue('');
      setIsErpIdSelected(false);
    }
  }, [isAddingFieldsActive]);

  useEffect(() => {
    if (!isFounded) return;

    const options: IKnmAutocompleteOption[] =
      isDecisionSearchEnabled && decisions?.length
        ? decisions.map(decision => {
            const decisionText = generateDecisionInfoText(decision);

            return {
              value: decisionText,
              label: decisionText,
              decision,
            };
          })
        : knms.map(knm => {
            const knmText = generateKnmInfoText(knm);

            return {
              value: knmText,
              label: knmText,
              knm,
            };
          });

    setKnmsAutocompleteOptions(options);
  }, [knms, decisions, isFounded]);

  useUnmount(() => {
    debouncedGetKnms.cancel();
    dispatch(knmListSetFindData([]));
    dispatch(decisionListSetSearchValue('', undefined));
  });

  useEffect(() => {
    knmsAutocompleteOptions && setLastKnmsAutocompleteOptions(knmsAutocompleteOptions);
  }, [knmsAutocompleteOptions]);

  const handleTextInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const tagName = (e?.relatedTarget as Element)?.tagName;

      if (tagName !== 'UL') {
        debouncedGetKnms('');
        setKnmsAutocompleteOptions(undefined);
      }
    },
    [debouncedGetKnms],
  );

  const handleTextInputClear = useCallback(() => {
    setErpIdInputValue('');
    setIsErpIdSelected(false);
    setIsKnmHasUnresolvedViolations(undefined);
    dispatch(knmErknmSetPrescriptionOldRules(false));
    resetNotAppliedPrescriptionState();
  }, [formRef, resetNotAppliedPrescriptionState]);

  const resetDeletionFlags = useCallback(() => {
    formRef?.current?.change('isObjectDeleted', false);
    formRef?.current?.change('isEventDeleted', false);
    formRef?.current?.change('isRequirementDeleted', false);
    formRef?.current?.change('isSubjectDeleted', false);
  }, [formRef]);

  const validateLinkKnm = useCallback(
    (knm: IKnmIndex, selectedKeys?: string[]) =>
      validateSelectedLinkKnm({
        knm,
        cachedLinkKnm: selectedLinkKnmCache,
        settings,
        reasonTypeName: reason?.type?.name,
        isPm,
        isErp,
        isEnablePaperlessDocument,
        erknmStartDateString,
        controllingOrganizationIds,
        kindControlCode,
        passportId,
        selectedPrescriptionKeys: selectedKeys,
      }),
    [
      reason?.type?.name,
      erknmStartDateString,
      isEnablePaperlessDocument,
      isPm,
      controllingOrganizationIds,
      kindControlCode,
      selectedLinkKnmCache,
      settings,
      passportId,
      isErp,
    ],
  );

  /** Валидация ссылки выполняется асинхронно, на это время сохранение блокируем */
  const [isSavingLink, setIsSavingLink] = useState(false);
  const saveRequestIdRef = useRef(0);

  useEffect(() => {
    dispatch(coreSetLoading(isSavingLink));

    return () => {
      if (isSavingLink) {
        dispatch(coreSetLoading(false));
      }
    };
  }, [dispatch, isSavingLink]);

  /** Обновляет уже добавленную ссылку на этот паспорт, иначе добавляет новую */
  const addOrReplaceLink = useCallback(
    (link: IKnmLink) => {
      const linkFieldName = linksName || 'links';
      // @ts-ignore
      const formLinks: IKnmLink[] | undefined = formRef?.current.getFieldState(linkFieldName)?.value;
      const linkIndex = formLinks?.findIndex(formLink => formLink.id === link.id);

      if (typeof linkIndex === 'number' && linkIndex >= 0) {
        // @ts-ignore
        formRef?.current.change(`${linkFieldName}[${linkIndex}]`, link);
      } else {
        push?.(linkFieldName, link);
      }
    },
    [formRef, linksName, push],
  );

  /** Сброс подбора ссылки (выполняется после завершения сохранения) */
  const resetLinkSelection = useCallback(() => {
    setErpIdInputValue('');
    setIsErpIdSelected(false);
    // Поле "Предписание" относится к сохранённой ссылке и должно остаться на экране
    if (!isPrescriptionPickerVisible) {
      resetPrescriptionState();
    }
  }, [isPrescriptionPickerVisible, resetPrescriptionState]);

  const addLink = useCallback(
    (knm: IKnmIndex) => {
      if (push) {
        const saveRequestId = saveRequestIdRef.current + 1;

        saveRequestIdRef.current = saveRequestId;
        setIsSavingLink(true);

        validateLinkKnm(knm, isSourceRaid ? selectedPrescriptionKeys : undefined)
          .then(valRes => {
            // Результат устаревшего сохранения применять нельзя, предписания могли уже измениться
            if (saveRequestId !== saveRequestIdRef.current) return;

            const link = {
              erpId: knm.erpId,
              id: knm.id,
              knmType: knm.knmType,
              subjectName: knm.organizationName,
              inn: knm.inn,
              ogrn: knm.ogrn,
              knoOrganizationName: knm.controllingOrganization,
              prosecutorOfficeName: knm.prosecutorOrganization,
              startDate: formatDateLocale({ date: knm.startDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
              stopDate: knm.stopDate
                ? formatDateLocale({ date: knm.stopDate, dateFormat: SYSTEM_DATE_FORMAT_FNS })
                : null,
              linkValidationMessage: typeof valRes === 'string' ? valRes : undefined,
            } as IKnmLink;

            addOrReplaceLink(link);

            dispatch(knmErknmSetPrescriptionOldRules(valRes === LEGACY_NO_PREFILL));

            if (typeof valRes !== 'object') {
              return;
            }

            // Набор предписаний считается применённым, только если данные действительно подтянулись
            if (isPrescriptionPickerVisible) {
              markPrescriptionsApplied();
            }

            const { isPrescriptionFailed, ...linkedData } = valRes;

            if (isPrescriptionFailed) {
              dispatch(knmErknmSetPrescriptionFailed(true));
            }

            const valResWithFillFlags = {
              ...linkedData,
              events: enrichmentData<IKnmEvent248>(linkedData.events),
              objects: enrichmentData<IKnmObject248>(linkedData.objects, true) as IKnmObject248[],
              requirements: enrichmentData<IKnmRequirementsNpa248 | IKnmRequirementsNpa>(linkedData.requirements),
              organizations: enrichmentData<IKnmSubject248>(linkedData.organizations, true) as IKnmSubject248[],
            };

            resetDeletionFlags();
            if (isPm) {
              dispatch(knmPmFillOrganizationsAndObjectsByOtherPassport(valResWithFillFlags, link));
            } else {
              dispatch(
                knmFillOrganizationsAndObjectsByOtherPassport(
                  valResWithFillFlags as {
                    organizations: IKnmSubject248[];
                    objects: IKnmObject248[];
                    events: IKnmEvent248[] | null;
                    requirements: IKnmRequirementsNpa248[] | null;
                  },
                  link,
                ),
              );
            }

            if (linkedData.requirements?.length && isNeedNapValidation) {
              dispatch(setIsNpaStructureChanged(true));
            }
          })
          .finally(() => {
            // Устаревшее сохранение не должно затирать уже начатый новый подбор
            if (saveRequestId !== saveRequestIdRef.current) return;

            setIsSavingLink(false);
            resetLinkSelection();
          });
      }
    },
    [
      push,
      isPm,
      isSourceRaid,
      selectedPrescriptionKeys,
      isPrescriptionPickerVisible,
      isNeedNapValidation,
      markPrescriptionsApplied,
      addOrReplaceLink,
      validateLinkKnm,
      resetLinkSelection,
      resetDeletionFlags,
      formRef,
      dispatch,
    ],
  );

  /** ТЗ 4.1.31: добавление ссылки на паспорт решения (ИР) с предзаполнением разделов КНМ */
  const addDecisionLink = useCallback(
    (decision: IDecisionListItem) => {
      if (!push) return;

      const saveRequestId = saveRequestIdRef.current + 1;

      saveRequestIdRef.current = saveRequestId;
      setIsSavingLink(true);

      validateSelectedLinkDecision({ decision, isPm })
        .then(valRes => {
          if (saveRequestId !== saveRequestIdRef.current) return;

          const link = {
            erpId: decision.number,
            id: decision.id,
            knmType: 'Decision',
            subjectName: decision.organizationName,
            inn: '',
            ogrn: '',
            knoOrganizationName: decision.kno,
            prosecutorOfficeName: decision.prosecutorOrganization,
            startDate: formatDateLocale({ date: decision.decisionDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
            stopDate: null,
            linkValidationMessage: typeof valRes === 'string' ? valRes : undefined,
          } as IKnmLink;

          addOrReplaceLink(link);

          if (typeof valRes !== 'object') {
            return;
          }

          formRef?.current?.change('isPassportByCheckPrescriptionFailed', true);

          const valResWithFillFlags = {
            ...valRes,
            events: enrichmentData<IKnmEvent248>(valRes.events),
            objects: enrichmentData<IKnmObject248>(valRes.objects, true) as IKnmObject248[],
            requirements: enrichmentData<IKnmRequirementsNpa248 | IKnmRequirementsNpa>(valRes.requirements),
            organizations: enrichmentData<IKnmSubject248>(valRes.organizations, true) as IKnmSubject248[],
          };

          resetDeletionFlags();
          dispatch(
            knmFillOrganizationsAndObjectsByOtherPassport(
              valResWithFillFlags as {
                organizations: IKnmSubject248[];
                objects: IKnmObject248[];
                events: IKnmEvent248[] | null;
                requirements: IKnmRequirementsNpa248[] | null;
              },
              link,
            ),
          );
        })
        .finally(() => {
          if (saveRequestId !== saveRequestIdRef.current) return;

          setIsSavingLink(false);
          resetLinkSelection();
        });
    },
    [push, isPm, addOrReplaceLink, resetDeletionFlags, resetLinkSelection, dispatch, formRef],
  );

  const deleteLink = (linkedErpId: string) => {
    // @ts-ignore
    const formLinks: IKnmLink[] | undefined = formRef?.current.getFieldState(linksName || 'links')?.value;
    const deletingLinkIndex = formLinks?.findIndex(formLink => formLink.erpId === linkedErpId);

    dispatch(knmErknmSetPrescriptionOldRules(false));

    if (typeof deletingLinkIndex === 'number' && deletingLinkIndex >= 0 && remove) {
      remove(linksName || 'links', deletingLinkIndex);
    }

    if (reason?.type?.name === KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS || isPrescriptionCheckReason) {
      // Предписания относились к удалённой ссылке
      resetPrescriptionState();
      resetDeletionFlags();
      if (isPm) {
        formRef?.current?.change('objectsPm');
        formRef?.current?.change('eventsPm');
        formRef?.current?.change('requirementsNpa');
        resetPmSubjectFields();
      } else {
        formRef?.current?.change('objectsErknm');
        formRef?.current?.change('eventsErknm');
        formRef?.current?.change('organizations');
        formRef?.current?.change('requirementsNpaErknm');
      }
    }
  };

  const resetPmSubjectFields = useCallback(() => {
    formRef?.current.change('subjectTypePm');
    formRef?.current.change('inn');
    formRef?.current.change('snils');
    formRef?.current.change('organizationName');
    formRef?.current.change('isFiz');
    formRef?.current.change('ogrn');
    formRef?.current.change('locationAddress');
    formRef?.current.change('workAddress');
    formRef?.current.change('country');
    formRef?.current.change('okveds');
    formRef?.current.change('kpp');
    formRef?.current.change('rafpNza');
    formRef?.current.change('document');
    formRef?.current.change('nkoForeignAgentsRegistryNumber');
    formRef?.current.change('nkoForeignAgentsRegistryInclusionDate');
    formRef?.current.change('nkoForeignAgentsRegistryExclusionDate');
    formRef?.current.change('isNko');
    formRef?.current.change('ervkId');
    formRef?.current.change('firstOrganizationName');
    formRef?.current.change('dateCreate');
    formRef?.current.change('mspCode');
    formRef?.current.change('mspRegistryDate');
    formRef?.current.change('checkSubjectResult');
    formRef?.current.change('isOrganizationNew');
  }, [formRef]);

  const handleSaveButtonClick = useCallback(() => {
    // Повторное применение изменённых предписаний: паспорт уже выбран, поле поиска очищено
    if (isReapplyPrescriptions && selectedKnmForLink) {
      addLink(selectedKnmForLink);

      return;
    }

    const autocompleteOption = lastKnmsAutocompleteOptions?.find(
      lastKnmAutocompleteOption => lastKnmAutocompleteOption.value === erpIdInputValue,
    );

    if (autocompleteOption?.decision) {
      addDecisionLink(autocompleteOption.decision);

      return;
    }

    if (autocompleteOption?.knm) {
      addLink(autocompleteOption.knm);
    }
  }, [
    addLink,
    addDecisionLink,
    erpIdInputValue,
    lastKnmsAutocompleteOptions,
    isReapplyPrescriptions,
    selectedKnmForLink,
  ]);

  const makeLinkDeleteButtonClickHandler = (linkedId: string) => () => {
    if (!watchOnly && linkedId && isEnabled) {
      deleteLink(linkedId);
    }
  };

  const isLinkOnOnlyPm = isPm && reason?.type?.name === KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS;

  const linkFieldLabel = useMemo(() => {
    if (isLinkOnOnlyPm) return 'Ссылка на другой паспорт ПМ';
    if (reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK) return 'Ссылка на другой паспорт';

    return 'Ссылка на другой паспорт КНМ или ПМ';
  }, [isLinkOnOnlyPm, reason?.type?.name]);

  const linkAutoCompleteNotFoundMessage = useMemo(() => {
    if (isLinkOnOnlyPm) return 'ПМ, соответствующие указанному учетному номеру, не найдены';
    if (reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK) {
      return 'Паспорта, соответствующие указанному учетному номеру, не найдены';
    }

    return 'КНМ или ПМ, соответствующие указанному учетному номеру, не найдены';
  }, [isLinkOnOnlyPm, reason?.type?.name]);

  return (
    <>
      <div>
        <div className={styles.DisplayNone}>
          <TextInputField name={`${reasonsFieldName}.linkValidation`} />
        </div>
        <div className={cn('shared-field-row', styles.LinkFields)}>
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: linkFieldLabel,
              field: linksName,
              isPm,
              isKnm,
              isDecision,
            })}
            placementLabel="left"
            color="black"
          >
            <AutoComplete
              type="area"
              options={knmsAutocompleteOptions}
              noFoundMessage={linkAutoCompleteNotFoundMessage}
              handleAutoCompleteSelect={handleAutoCompleteSelect}
            >
              <TextInput
                id="linkErpId"
                name="linkErpId"
                placeholder="Введите номер паспорта, выберите из выпадающего списка"
                clearable={isErpIdSelected}
                value={erpIdInputValue}
                onBlur={handleTextInputBlur}
                onChange={handleTextInputChange}
                onClear={handleTextInputClear}
                disabled={watchOnly || isAddDisabled || isSavingLink}
                error={
                  isKnmHasUnresolvedViolations === false
                    ? 'Выбранный паспорт не содержит нарушений в статусах «Не устранено» или «Ожидает подтверждения», срок устранения которых уже наступил'
                    : undefined
                }
                invalid={isKnmHasUnresolvedViolations === false}
              />
            </AutoComplete>
          </FieldBlock>
          <Button
            className={styles.SaveButton}
            text="Сохранить"
            color="default"
            size="medium"
            type="button"
            id="saveButton"
            disabled={
              watchOnly ||
              !isEnabled ||
              isSavingLink ||
              isKnmHasUnresolvedViolations === false ||
              (isPrescriptionPickerVisible && !isPrescriptionsFilled) ||
              (!isReapplyPrescriptions && (!isErpIdSelected || isAddDisabled))
            }
            onClick={handleSaveButtonClick}
          />
          <Button
            className={styles.CancelButton}
            variant="link"
            color="primary"
            text="Отмена"
            size="medium"
            type="button"
            onClick={closeLinkFields}
          />
        </div>
        <FormSpy subscription={{ errors: true, touched: true }}>
          {props => {
            if (!linksName || !props.errors) return null;

            const fieldError = getIn(props.errors, linksName);
            const isFieldTouched = true;

            if (isFieldTouched && fieldError) {
              return (
                <Row>
                  <p className="shared-input-error">{fieldError}</p>
                </Row>
              );
            }

            return null;
          }}
        </FormSpy>
      </div>

      <FieldArray
        name={linksName || 'links'}
        validate={isAvailableInStatusCommentsExist ? requiredRequirementsErknmOrApproveDocsOrLinks : () => undefined}
        key={isAvailableInStatusCommentsExist ? 0 : 1}
      >
        {({ fields }) => (
          <div>
            <ul className={styles.Links}>
              {fields.map((name, index) => {
                const link = fields.value?.[index] as IKnmLink | undefined;
                const linkId = link?.id || '';
                const isPmInner = link?.knmType === null;

                let toPath = '';

                if (link?.knmType === 'Decision') {
                  toPath = ROUTE_DECISION(linkId);
                } else if (isPmInner) {
                  toPath = ROUTE_PM(linkId);
                } else {
                  toPath = ROUTE_KNM(linkId);
                }

                return (
                  <>
                    <li className={styles.Link} key={name}>
                      <Link className={styles.IdLink} to={toPath} target="blank">
                        {fields.value?.[index]?.erpId}
                      </Link>
                      <div className={styles.LinkText}>{generateLinkInfoText(fields.value?.[index])}</div>
                      <CloseButton
                        className={styles.LinkDeleteButton}
                        disabled={watchOnly || !isEnabled}
                        onClick={makeLinkDeleteButtonClickHandler(fields.value?.[index]?.erpId)}
                      />
                    </li>
                  </>
                );
              })}
            </ul>
            <FormSpy subscription={{ errors: true, touched: true }}>
              {props => {
                if (!linksName || !props.errors) return null;

                const fieldError = getIn(props.errors, linkValidationFieldName);

                const isFieldTouched = true;

                if (isFieldTouched && fieldError) {
                  return (
                    <Row>
                      {/* eslint-disable-next-line react/no-danger */}
                      <div className="shared-input-error" dangerouslySetInnerHTML={{ __html: fieldError }} />
                    </Row>
                  );
                }

                if (fields.value?.[0]?.erpId) {
                  if (reason?.type?.name === KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS) {
                    return (
                      <Hint text={isLinkOnOnlyPm ? INFORMATION_ABOUT_LINK_DATA_PM : INFORMATION_ABOUT_LINK_DATA_KNM} />
                    );
                  }
                  if (isPrescriptionCheckReason && !isErp && isPassportByCheckPrescriptionOldRules === false) {
                    return <Hint text={INFORMATION_ABOUT_COPY_DATA_BY_PRESCRIPTION} />;
                  }
                }

                return null;
              }}
            </FormSpy>
          </div>
        )}
      </FieldArray>

      {isPrescriptionPickerVisible && (
        <LinkPrescriptions
          options={prescriptionOptions}
          isEditable={isPrescriptionEditable}
          isCurrentRaid={isCurrentRaid}
          isChanged={isPrescriptionsChanged}
          linkFieldLabel={linkFieldLabel}
          watchOnly={watchOnly}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmLinksStoreProps => ({
  formRef: getKnmFormRef(state),
  knms: getKnmListFindData(state),
  isAvailableInStatusCommentsExist: getKnmIsEditableInStatusCommentsExist(state),
  settings: getAdminFetchSettings(state),
  isPm: getKnmSystemType(state) === KnmSystemTypesEnum.PM,
  passportId: getKnmData(state)?.id,
  isKnm: getKnmSystemType(state) === KnmSystemTypesEnum.ERKNM,
  isDecision: !!getDecisionFormRef(state),
  decisions: getDecisionListData(state),
  isPassportByCheckPrescriptionOldRules: getKnmErknmIsPrescriptionOldRules(state),
});

export const ErknmLinks = connect(mapStateToProps)(ErknmLinksWithoutStore);
